@if (isSavingUser() || isDeletingUser() || isDeletingGroup() || isSavingGroup()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div class="p-8 bg-white rounded-sm shadow-lg">
      <axpo-spinner size="normal">
        {{ 'core.loading' | transloco }}
      </axpo-spinner>
    </div>
  </div>
}

<section class="bg-white dark:bg-gray-900">
  <div class="container px-4 py-8 mx-auto lg:py-8 lg:px-6">
    <div class="flex items-center justify-between mb-4">
      <axpo-typography
        [variant]="'title-xl'"
        [weight]="'bold'"
        data-testid="contact-management-heading"
        class="block"
      >
        {{ 'contactManagement.title' | transloco }}
      </axpo-typography>

      <axpo-button
        type="filled"
        [style]="'primary'"
        class="flex-none"
        (click)="openAdd()"
        data-testid="add-button"
      >
        <span class="inline-flex items-center gap-2">
          <div class="w-5 h-5">
            <div [innerHTML]="getSvg()[isGroupsTab() ? 'add-group' : 'user-add']"></div>
          </div>
          @if (isGroupsTab()) {
            {{ 'contactManagement.addGroup' | transloco }}
          } @else {
            {{ 'contactManagement.addUser' | transloco }}
          }
        </span>
      </axpo-button>
    </div>

    <app-tabs
      (tabSelected)="tabSelectionChanged($event)"
      [tabItems]="tabs()"
      [selectedTabId]="selectedTabId()"
    ></app-tabs>

    <div class="mt-8">
      @if (selectedTabId() === 'users') {
        <div class="flex flex-col justify-center gap-4 mb-6 sm:flex-row">
          <form autocomplete="off" (submit)="$event.preventDefault()">
            <div class="flex-1 max-w-80">
              <axpo-search
                [(searchValue)]="searchTerm"
                [debounceTime]="500"
                [debounce]="true"
                (debounceValue)="search()"
                placeholder="{{ 'contactManagement.searchPlaceholder' | transloco }}"
              ></axpo-search>
            </div>
          </form>
        </div>
        @if (isInitialLoad()) {
          <div class="flex justify-center py-8">
            <axpo-spinner size="normal">
              {{ 'core.loading' | transloco }}
            </axpo-spinner>
          </div>
        } @else if (users()?.length) {
          @for (user of users(); track user.id) {
            <div class="m-4 flex justify-between border-b-2 pb-2">
              <div class="w-1/3">
                <axpo-typography variant="body-m">
                  {{ user.name }}
                </axpo-typography>
                <div class="min-h-4">
                  @if (user.designation) {
                    <axpo-typography variant="body-s">
                      {{ user.designation }}
                    </axpo-typography>
                  }
                </div>
              </div>
              <div class="w-1/3">
                @if (user.email) {
                  <axpo-typography>{{ user.email }}</axpo-typography>
                }
                @if (user.phone) {
                  <axpo-typography>{{ user.phone }}</axpo-typography>
                }
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="w-6 h-6 text-primary hover:text-text-link hover:cursor-pointer"
                  (click)="editUser(user)"
                  [attr.aria-label]="'contactManagement.editUser' | transloco"
                >
                  <div [innerHTML]="getSvg()['edit']"></div>
                </button>
                <button
                  type="button"
                  class="w-6 h-6 text-primary hover:text-text-link hover:cursor-pointer"
                  (click)="deleteUser(user)"
                  [attr.aria-label]="'contactManagement.deleteUser' | transloco"
                >
                  <div [innerHTML]="getSvg()['delete']"></div>
                </button>
              </div>
            </div>
          }

          <axpo-tablenavigation
            [(page)]="page"
            [(pageSize)]="pageSize"
            [totalItems]="totalUsers()"
            [style]="'default'"
          />
        } @else {
          <axpo-typography [variant]="'body-l'">
            {{ 'contactManagement.noUsers' | transloco }}
          </axpo-typography>
        }
      } @else {
        <div class="flex flex-col justify-center gap-4 mb-6 sm:flex-row">
          <form autocomplete="off" (submit)="$event.preventDefault()">
            <div class="flex-1 max-w-80">
              <axpo-search
                [(searchValue)]="groupSearchTerm"
                [debounceTime]="500"
                [debounce]="true"
                (debounceValue)="groupSearch()"
                placeholder="{{ 'contactManagement.groupsSearchPlaceholder' | transloco }}"
              ></axpo-search>
            </div>
          </form>
        </div>
        @if (isGroupsInitialLoad()) {
          <div class="flex justify-center py-8">
            <axpo-spinner size="normal">
              {{ 'core.loading' | transloco }}
            </axpo-spinner>
          </div>
        } @else if (groups()?.length) {
          @for (group of groups(); track group.id) {
            <div class="m-4 flex justify-between border-b-2 pb-2">
              <div class="w-2/3 m-2">
                <axpo-typography variant="body-m">
                  {{ group.name }}
                </axpo-typography>
              </div>
              <div class="flex items-center gap-1">
                <button
                  type="button"
                  class="w-6 h-6 text-primary hover:text-text-link hover:cursor-pointer"
                  (click)="editGroup(group)"
                  [attr.aria-label]="'contactManagement.editGroup' | transloco"
                >
                  <div [innerHTML]="getSvg()['edit']"></div>
                </button>
                <button
                  type="button"
                  class="w-6 h-6 text-primary hover:text-text-link hover:cursor-pointer"
                  (click)="deleteGroup(group)"
                  [attr.aria-label]="'contactManagement.deleteGroup' | transloco"
                >
                  <div [innerHTML]="getSvg()['delete']"></div>
                </button>
              </div>
            </div>
          }

          <axpo-tablenavigation
            [(page)]="groupPage"
            [(pageSize)]="groupPageSize"
            [totalItems]="totalGroups()"
            [style]="'default'"
          />
        } @else {
          <axpo-typography [variant]="'body-l'">
            {{ 'contactManagement.noGroups' | transloco }}
          </axpo-typography>
        }
      }
    </div>
  </div>
</section>

@if (showUserDialog()) {
  <axpo-dialog
    title="{{ dialogTitle() | transloco }}"
    cancelText="{{ 'core.cancel' | transloco }}"
    confirmText="{{ 'core.save' | transloco }}"
    textAlign="left"
    (takenAction)="closeUserDialog($event)"
    [saveDisabled]="saveDisabled()"
  >
    <form autocomplete="off" (submit)="$event.preventDefault()">
      <div class="flex flex-col space-y-4">
        <axpo-form-element
          formId="name"
          label="{{ 'contactManagement.name' | transloco }}"
          formType="text"
          [(value)]="formName"
          [min]="1"
          [max]="100"
          (errorEmitter)="formValidation($event, 'name')"
          [errorMessageInput]="formManager?.getField('name')?.errorMessage()"
          [required]="true"
        ></axpo-form-element>
        <axpo-form-element
          formId="email"
          label="{{ 'contactManagement.email' | transloco }}"
          formType="email"
          pattern="^(?:[a-zA-Z0-9_%+\-]+(?:\.[a-zA-Z0-9_%+\-]+)*@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})?$"
          [(value)]="formEmail"
          (errorEmitter)="formValidation($event, 'email')"
          [errorMessageInput]="formManager?.getField('email')?.errorMessage()"
          [required]="true"
        ></axpo-form-element>
        <axpo-form-element
          formId="phone"
          label="{{ 'contactManagement.phone' | transloco }}"
          formType="tel"
          pattern="^(?:\+\d{5,18})?$"
          [(value)]="formPhone"
          [max]="18"
          (errorEmitter)="formValidation($event, 'phone')"
          [errorMessageInput]="formManager?.getField('phone')?.errorMessage()"
          [required]="true"
        ></axpo-form-element>
        <axpo-form-element
          formId="designation"
          label="{{ 'contactManagement.designation' | transloco }}"
          formType="text"
          [(value)]="formDesignation"
          [min]="1"
          [max]="100"
          (errorEmitter)="formValidation($event, 'designation')"
          [errorMessageInput]="formManager?.getField('designation')?.errorMessage()"
          [required]="true"
        ></axpo-form-element>
      </div>
    </form>
  </axpo-dialog>
}

@if (showDeleteDialog()) {
  <axpo-dialog
    title="{{ 'contactManagement.deleteUserDialogTitle' | transloco }}"
    cancelText="{{ 'core.cancel' | transloco }}"
    confirmText="{{ 'contactManagement.deleteUser' | transloco }}"
    textAlign="left"
    (takenAction)="deleteUserDialogClosed($event)"
  >
    <div class="flex flex-col space-y-4">
      <axpo-typography [variant]="'body-m'">
        {{ 'contactManagement.deleteUserDialogText' | transloco }}
      </axpo-typography>
    </div>
  </axpo-dialog>
}

@if (showDeleteGroupDialog()) {
  <axpo-dialog
    title="{{ 'contactManagement.deleteGroupDialogTitle' | transloco }}"
    cancelText="{{ 'core.cancel' | transloco }}"
    confirmText="{{ 'contactManagement.deleteGroup' | transloco }}"
    textAlign="left"
    (takenAction)="deleteGroupDialogClosed($event)"
  >
    <div class="flex flex-col space-y-4">
      <axpo-typography [variant]="'body-m'">
        {{ 'contactManagement.deleteGroupDialogText' | transloco }}
      </axpo-typography>
    </div>
  </axpo-dialog>
}

@if (showGroupDialog()) {
  <axpo-dialog
    title="{{ groupDialogTitle() | transloco }}"
    cancelText="{{ 'core.cancel' | transloco }}"
    [confirmText]="(editingGroup()?.id ? 'core.save' : 'contactManagement.createGroup') | transloco"
    textAlign="left"
    (takenAction)="closeGroupDialog($event)"
    [saveDisabled]="groupSaveDisabled()"
    [keepOpenOnConfirm]="keepGroupDialogOpen()"
  >
    <form autocomplete="off" (submit)="$event.preventDefault()">
      <div class="pb-4">
        <div class="flex flex-col space-y-4">
          <axpo-form-element
            formId="groupName"
            label="{{ 'contactManagement.groupName' | transloco }}"
            formType="text"
            [(value)]="formGroupName"
            [min]="1"
            [max]="100"
            [required]="true"
          ></axpo-form-element>
          @if (editingGroup()?.id) {
            <div class="pt-2">
              <axpo-typography variant="body-m" weight="bold">
                {{ 'contactManagement.groupMembers' | transloco }}
              </axpo-typography>
              @if (isLoadingGroupDetail()) {
                <div class="flex justify-center py-4">
                  <axpo-spinner size="xs">
                    {{ 'core.loading' | transloco }}
                  </axpo-spinner>
                </div>
              } @else if (groupMembers().length) {
                <div
                  class="mt-2 flex max-h-[min(9rem,18vh)] min-h-0 flex-col divide-y divide-border overflow-y-auto"
                >
                  @for (member of groupMembers(); track member.id) {
                    <div class="flex items-center justify-between gap-3 py-1 pr-3">
                      <div class="flex flex-col">
                        <axpo-typography variant="body-m">
                          {{ member.name }}
                        </axpo-typography>
                        <div class="mt-1 flex flex-col">
                          @if (member.type) {
                            <axpo-typography variant="body-s">
                              {{ 'contactManagement.memberType' | transloco }}: {{ member.type }}
                            </axpo-typography>
                          }
                          @if (member.email) {
                            <axpo-typography variant="body-s">
                              {{ 'contactManagement.email' | transloco }}: {{ member.email }}
                            </axpo-typography>
                          }
                        </div>
                      </div>
                      <button
                        type="button"
                        class="w-6 h-6 shrink-0 text-primary hover:text-text-link hover:cursor-pointer"
                        (click)="removeGroupMember(member)"
                        [attr.aria-label]="'contactManagement.removeMember' | transloco"
                        [disabled]="isRemovingGroupMember()"
                      >
                        <div [innerHTML]="getSvg()['delete']"></div>
                      </button>
                    </div>
                  }
                </div>
              } @else {
                <axpo-typography variant="body-s">
                  {{ 'contactManagement.noGroupMembers' | transloco }}
                </axpo-typography>
              }

              <div class="pt-4 border-t border-border mt-4">
                <axpo-typography variant="body-m" weight="bold">
                  {{ 'contactManagement.addMembers' | transloco }}
                </axpo-typography>
                <div class="mt-2 flex-1 max-w-80">
                  <axpo-search
                    [(searchValue)]="memberSearchTerm"
                    [debounceTime]="500"
                    [debounce]="true"
                    (debounceValue)="searchMembers()"
                    placeholder="{{ 'contactManagement.searchMemberPlaceholder' | transloco }}"
                  ></axpo-search>
                </div>

                @if (isSearchingMembers()) {
                  <div class="flex justify-center py-4">
                    <axpo-spinner size="xs">
                      {{ 'core.loading' | transloco }}
                    </axpo-spinner>
                  </div>
                } @else if (searchResults().length) {
                  <div
                    class="mt-2 flex h-[min(8rem,16vh)] min-h-0 flex-col divide-y divide-border overflow-y-auto"
                  >
                    @for (result of searchResults(); track $index) {
                      <div class="flex items-center justify-between gap-3 py-1 pr-3">
                        <div class="flex flex-col">
                          <axpo-typography variant="body-m">
                            {{ result.name }}
                          </axpo-typography>
                          <div class="mt-1 flex flex-col">
                            @if (result.type) {
                              <axpo-typography variant="body-s">
                                {{ 'contactManagement.memberType' | transloco }}: {{ result.type }}
                              </axpo-typography>
                            }
                            @if (result.email) {
                              <axpo-typography variant="body-s">
                                {{ 'contactManagement.email' | transloco }}: {{ result.email }}
                              </axpo-typography>
                            }
                          </div>
                        </div>
                        <button
                          type="button"
                          class="w-6 h-6 shrink-0 text-primary hover:text-text-link hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                          (click)="addMember(result)"
                          [disabled]="isAddingGroupMember()"
                          [attr.aria-label]="'contactManagement.addMember' | transloco"
                          [title]="'contactManagement.addMember' | transloco"
                        >
                          <div [innerHTML]="getSvg()['user-add']"></div>
                        </button>
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </form>
  </axpo-dialog>
}
